import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Category, CategoryId, NewExpense, QuickAdd } from "../api";
import {
  OTHER_CATEGORY_ID,
  catMap,
  fmtFull,
  todayLocalISO,
} from "../constants";
import { useVisualViewport } from "../hooks";
import { Icon, btnPrimary } from "../icons";

interface Props {
  username?: string;
  categories: Category[];
  quickAdds: QuickAdd[];
  isDesktop: boolean;
  onClose: () => void;
  onAdd: (exp: NewExpense) => void | Promise<void>;
}

export function AddExpenseModal({
  onClose,
  onAdd,
  username,
  categories,
  quickAdds,
  isDesktop,
}: Props) {
  const { t } = useTranslation();
  const vv = useVisualViewport();
  const [amount, setAmount] = useState("");
  const [cat, setCat] = useState<CategoryId>(
    categories[0]?.id ?? OTHER_CATEGORY_ID,
  );
  const [note, setNote] = useState("");
  const [date, setDate] = useState(todayLocalISO());
  const [submitting, setSubmitting] = useState(false);
  const catLookup = useMemo(() => catMap(categories), [categories]);

  const amountNum = parseFloat(amount) || 0;
  const valid = amountNum > 0 && /^\d{4}-\d{2}-\d{2}$/.test(date);

  const applyChip = (chip: QuickAdd) => {
    setAmount(String(chip.amount));
    setCat(chip.cat);
    setNote(chip.label);
  };

  const submit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      await onAdd({ amount: amountNum, cat, note: note.trim(), date });
    } finally {
      setSubmitting(false);
    }
  };

  const sheetStyle: React.CSSProperties = isDesktop
    ? {
        position: "relative",
        zIndex: 1,
        width: "min(520px, calc(100vw - 32px))",
        maxHeight: "calc(100vh - 48px)",
        overflowY: "auto",
        background: "var(--surface)",
        borderRadius: 18,
        padding: "8px 0 24px",
        boxShadow: "0 24px 60px rgba(0,0,0,0.22), 0 4px 12px rgba(0,0,0,0.08)",
        animation: "pop .25s cubic-bezier(.2,.8,.2,1) both",
      }
    : {
        position: "relative",
        zIndex: 1,
        width: "100%",
        maxWidth: 480,
        height: "100%",
        overflowY: "auto",
        overscrollBehavior: "contain",
        WebkitOverflowScrolling: "touch",
        background: "var(--bg)",
        borderTopLeftRadius: 22,
        borderTopRightRadius: 22,
        padding: "8px 0 24px",
        boxShadow: "0 -10px 40px rgba(0,0,0,0.18)",
        animation: "slideUp .3s cubic-bezier(.2,.8,.2,1) both",
      };

  const dialogStyle: React.CSSProperties = isDesktop
    ? {
        position: "fixed",
        inset: 0,
        zIndex: 80,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }
    : {
        position: "fixed",
        top: `${vv.offsetTop}px`,
        left: `${vv.offsetLeft}px`,
        width: `${vv.width}px`,
        height: `${vv.height}px`,
        zIndex: 80,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "center",
      };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("addExpense.modalLabel")}
      style={dialogStyle}
    >
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(20, 20, 18, 0.4)",
          animation: "fade .25s ease both",
        }}
      />

      <div style={sheetStyle}>
        {!isDesktop && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              paddingTop: 6,
              paddingBottom: 4,
            }}
          >
            <div style={{ width: 40, height: 4, borderRadius: 999, background: "var(--line)" }} />
          </div>
        )}

        <div
          style={{
            padding: "8px 22px 12px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ fontSize: 21, fontWeight: 600, letterSpacing: "-.01em" }}>
            {t("addExpense.title")}
          </div>
          <button
            onClick={onClose}
            aria-label={t("addExpense.close")}
            style={{
              width: 32,
              height: 32,
              borderRadius: 999,
              background: "#e8e6df",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {Icon.close()}
          </button>
        </div>

        <div style={{ padding: "6px 22px 0" }}>
          <div
            style={{
              background: "var(--surface-2)",
              borderRadius: 14,
              border: "1px solid var(--line)",
              padding: "20px 18px",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span className="num" style={{ fontSize: 38, fontWeight: 500, color: "var(--muted)" }}>
              €
            </span>
            <input
              type="number"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              autoFocus
              aria-label={t("addExpense.amount")}
              className="num"
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                background: "transparent",
                fontSize: 38,
                fontWeight: 500,
                color: "var(--ink)",
                letterSpacing: "-.02em",
                minWidth: 0,
              }}
            />
          </div>
        </div>

        {quickAdds.length > 0 && (
          <div style={{ padding: "14px 22px 4px" }}>
            <SubLabel>{t("addExpense.quickAdd")}</SubLabel>
            <div
              style={{
                display: "flex",
                gap: 8,
                overflowX: "auto",
                paddingBottom: 4,
                marginRight: -22,
              }}
            >
              {quickAdds.map((c, i) => (
                <button
                  key={i}
                  onClick={() => applyChip(c)}
                  style={{
                    flexShrink: 0,
                    padding: "8px 14px",
                    borderRadius: 999,
                    background: "var(--surface)",
                    border: "1px solid var(--line)",
                    fontSize: 13,
                    fontWeight: 500,
                    color: "var(--ink)",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>
                    {catLookup[c.cat]?.emoji ?? "•"}
                  </span>
                  <span>{c.label}</span>
                  <span className="num" style={{ color: "var(--muted)" }}>
                    €{c.amount}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div style={{ padding: "14px 22px 0" }}>
          <SubLabel>{t("addExpense.category")}</SubLabel>
          <div style={{ position: "relative" }}>
            <select
              value={cat}
              onChange={(e) => setCat(e.target.value)}
              aria-label={t("addExpense.category")}
              style={{
                width: "100%",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
                border: "1px solid var(--line)",
                background: "var(--surface)",
                borderRadius: 12,
                padding: "12px 38px 12px 14px",
                fontSize: 16,
                outline: "none",
                color: "var(--ink)",
                cursor: "pointer",
              }}
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.emoji}  {c.label}
                </option>
              ))}
            </select>
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                top: "50%",
                right: 14,
                transform: "translateY(-50%)",
                fontSize: 12,
                color: "var(--muted)",
                pointerEvents: "none",
              }}
            >
              ▾
            </span>
          </div>
        </div>

        <div style={{ padding: "14px 22px 0" }}>
          <SubLabel>{t("addExpense.date")}</SubLabel>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            max={todayLocalISO()}
            aria-label={t("addExpense.date")}
            className="num"
            style={{
              width: "100%",
              border: "1px solid var(--line)",
              background: "var(--surface)",
              borderRadius: 12,
              padding: "12px 14px",
              fontSize: 16,
              outline: "none",
              color: "var(--ink)",
            }}
          />
        </div>

        <div style={{ padding: "14px 22px 0" }}>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t("addExpense.notePlaceholder")}
            aria-label={t("addExpense.note")}
            style={{
              width: "100%",
              border: "1px solid var(--line)",
              background: "var(--surface)",
              borderRadius: 12,
              padding: "12px 14px",
              fontSize: 16,
              outline: "none",
              color: "var(--ink)",
            }}
          />
        </div>

        <div style={{ padding: "18px 22px 0" }}>
          <button disabled={!valid || submitting} onClick={submit} style={btnPrimary(valid && !submitting)}>
            {submitting
              ? t("addExpense.adding")
              : valid
                ? t("addExpense.addAmount", { amount: fmtFull(amountNum) })
                : t("addExpense.addButton")}
          </button>
        </div>
      </div>
    </div>
  );
}

function SubLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        color: "var(--muted)",
        textTransform: "uppercase",
        letterSpacing: ".08em",
        fontWeight: 500,
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
}
